import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Send, ShieldAlert } from "lucide-react";
import { SitePage, PageHead } from "./SiteChrome";
import { INQUIRY_TYPES } from "./siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACK_TEXT =
  "I understand that this form is for general business inquiries only and must not contain patient information, protected health information, medical records, or clinical case details.";

export default function ContactPage() {
  const [params] = useSearchParams();
  const preset = INQUIRY_TYPES.find((t) => t.k === params.get("type"))?.k || "general";
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", inquiry: preset });
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!acknowledged) {
      setError("Please accept the business-inquiry acknowledgment before sending.");
      return;
    }
    setLoading(true);
    const label = INQUIRY_TYPES.find((t) => t.k === form.inquiry)?.label || "General";
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        organization: form.organization,
        role: form.role,
        inquiry_type: label,
        acknowledged: true,
      });
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SitePage testId="contact-page">
      <PageHead
        label="Contact"
        title={<>Let&apos;s talk. <span className="grad-text">We read everything.</span></>}
        lede="Beta access, partnerships, investment, media, or professional questions — tell us who you are and we'll reach out directly."
      />

      <div className="sp-contact-grid">
        {success ? (
          <div className="sp-form sp-form-success" data-testid="contact-success">
            <h3>✓ Inquiry received.</h3>
            <p>Thank you — we&apos;ll be in touch at <strong>{form.email}</strong> shortly.</p>
          </div>
        ) : (
          <form className="sp-form" onSubmit={submit} data-testid="contact-form">
            <div className="sp-form-row">
              <div className="sp-field">
                <label>Name</label>
                <input required value={form.name} onChange={set("name")} placeholder="Dr. Jane Rivera" data-testid="contact-name-input" />
              </div>
              <div className="sp-field">
                <label>Email</label>
                <input required type="email" value={form.email} onChange={set("email")} placeholder="you@practice.com" data-testid="contact-email-input" />
              </div>
            </div>
            <div className="sp-form-row">
              <div className="sp-field">
                <label>Organization</label>
                <input value={form.organization} onChange={set("organization")} placeholder="Practice, group, or company" data-testid="contact-org-input" />
              </div>
              <div className="sp-field">
                <label>Professional role</label>
                <input value={form.role} onChange={set("role")} placeholder="e.g. Prosthodontist, Practice Manager" data-testid="contact-role-input" />
              </div>
            </div>
            <div className="sp-field">
              <label>Inquiry type</label>
              <div className="sp-inquiry-row">
                {INQUIRY_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.k}
                    className={`sp-inquiry-chip ${form.inquiry === t.k ? "is-active" : ""}`}
                    onClick={() => setForm((f) => ({ ...f, inquiry: t.k }))}
                    data-testid={`contact-inquiry-${t.k}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="sp-ack" data-testid="contact-ack-label">
              <input
                type="checkbox"
                required
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                data-testid="contact-ack-checkbox"
              />
              <span>{ACK_TEXT}</span>
            </label>

            <div className="sp-phi-notice" data-testid="contact-phi-notice">
              <ShieldAlert size={14} />
              <span>This form intentionally has no message field. Do not attempt to submit patient information or protected health information.</span>
            </div>
            {error && <div className="dw-error" data-testid="contact-error">{error}</div>}
            <button className="btn-primary sp-btn sp-submit" type="submit" disabled={loading} data-testid="contact-submit-btn">
              <Send size={14} /> {loading ? "Sending…" : "Send inquiry"}
            </button>
          </form>
        )}

        <aside className="sp-contact-aside">
          <div className="trust-card">
            <div>
              <div className="trust-card-title">Direct</div>
              <div className="trust-card-sub"><a href="mailto:ronik@camelionai.com" className="sp-link">ronik@camelionai.com</a><br />Boca Raton, FL</div>
            </div>
          </div>
          <div className="trust-card">
            <div>
              <div className="trust-card-title">Beta programs</div>
              <div className="trust-card-sub">ProsthoAI, ZidaMD, and ReimbursePro beta access is live. Select “Beta access” and we&apos;ll follow up by email.</div>
            </div>
          </div>
          <div className="trust-card">
            <div>
              <div className="trust-card-title">Response time</div>
              <div className="trust-card-sub">We aim to respond within two business days.</div>
            </div>
          </div>
        </aside>
      </div>
    </SitePage>
  );
}
